import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { formatPercentage } from '../utils'
import formatError from '../../../utilities/formatError'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Select } from '../../select'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const validationSchema = {
  rent: { min: 0, max: 999999999 },
  fees: { min: 0, max: 999999999 },
  rentIns: { min: 0, max: 999999999 },
  rentIncr: { min: 0, max: 10 },
  purchase: { min: 0, max: 999999999 },
  growth: { min: 0, max: 10 },
  realtorFees: { min: 0, max: 100 },
  loan: { min: 0, max: 999999999 },
  rate: { min: 0, max: 40 },
  term: { min: 1, max: 40 },
  closingCosts: { min: 0, max: 999999999 },
  homeowners: { min: 0, max: 999999999 },
  taxes: { min: 0, max: 999999999 },
  maint: { min: 0, max: 999999999 },
  analysis: { min: 1, max: 30 },
  taxBracket: { min: 0, max: 75 },
  beforeTaxReturn: { min: -12, max: 12 },
  inf: { min: 0, max: 10 },
}

const RentOrBuyCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      rent: 0,
      fees: 0,
      rentIns: 0,
      rentIncr: 0,
      purchase: 0,
      growth: 5,
      realtorFees: 6,
      loan: 0,
      rate: 5,
      term: 30,
      closingCosts: 3,
      homeowners: 0,
      taxes: 0,
      maint: 0,
      analysis: 30,
      taxBracket: 22,
      beforeTaxReturn: 8,
      inf: 3.5,
      standardDeduction: 'Y',
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)

    try {
      const url = 'https://www.calcxml.com/rest/hom06'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Hom06JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        rent: formData.rent,
        fees: formData.fees,
        rentIns: formData.rentIns,
        rentIncr: formatPercentage(formData.rentIncr),
        purchase: formData.purchase,
        growth: formatPercentage(formData.growth),
        realtorFees: formatPercentage(formData.realtorFees),
        loan: formData.loan,
        rate: formatPercentage(formData.rate),
        term: formData.term,
        closingCosts: formData.closingCosts,
        homeowners: formData.homeowners,
        taxes: formData.taxes,
        maint: formData.maint,
        analysis: formData.analysis,
        taxBracket: formatPercentage(formData.taxBracket),
        beforeTaxReturn: formatPercentage(formData.beforeTaxReturn),
        inf: formatPercentage(formData.inf),
        standardDeduction: formData.standardDeduction,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('RentOrBuyCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('RentOrBuyCalculator.firstLegend', {
              defaultValue: 'Rent assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.monthlyRentPaymentLabel', {
                  defaultValue: 'Monthly rent payment ($)',
                })}
                id="rent"
                name="rent"
                type="number"
                min="0"
                ref={register(validationSchema.rent)}
                error={formatError(errors.rent, validationSchema.rent, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.additionalMonthlyFeesLabel', {
                  defaultValue: 'Additional monthly fees ($)',
                })}
                id="fees"
                name="fees"
                type="number"
                min="0"
                ref={register(validationSchema.fees)}
                error={formatError(errors.fees, validationSchema.fees, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.monthlyRenterInsurancePremiumLabel', {
                  defaultValue: "Monthly renter's insurance premium ($)",
                })}
                id="rentIns"
                name="rentIns"
                type="number"
                min="0"
                ref={register(validationSchema.rentIns)}
                error={formatError(errors.rentIns, validationSchema.rentIns, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualRentIncreasesLabel', {
                  defaultValue: 'Annual rent increases (0% to 10%)',
                })}
                id="rentIncr"
                name="rentIncr"
                type="number"
                min="0"
                max=".10"
                step="0.01"
                ref={register(validationSchema.rentIncr)}
                error={formatError(errors.rentIncr, validationSchema.rentIncr, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('RentOrBuyCalculator.secondLegend', {
              defaultValue: 'Purchase assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.purchasePriceHomeLabel', {
                  defaultValue: 'Purchase price of home ($)',
                })}
                id="purchase"
                name="purchase"
                type="number"
                ref={register(validationSchema.purchase)}
                error={formatError(errors.purchase, validationSchema.purchase, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualAppreciationRateLabel', {
                  defaultValue: 'Annual appreciation rate (0% to 10%)',
                })}
                id="growth"
                name="growth"
                type="number"
                min="0"
                max=".10"
                ref={register(validationSchema.growth)}
                error={formatError(errors.growth, validationSchema.growth, t)}
                autoSelect
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.realtorFeesOnFutureSaleLabel', {
                  defaultValue: 'Realtor fees on future sale (typically 6%)',
                })}
                id="realtorFees"
                name="realtorFees"
                type="number"
                min="0"
                max="1"
                ref={register(validationSchema.realtorFees)}
                error={formatError(errors.realtorFees, validationSchema.realtorFees, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.amountOfLoanLabel', {
                  defaultValue: 'Mortgage loan amount (house price minus your down payment)',
                })}
                id="loan"
                name="loan"
                type="number"
                min="0"
                ref={register(validationSchema.loan)}
                error={formatError(errors.loan, validationSchema.loan, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualInterestRateLabel', {
                  defaultValue: 'Annual interest rate (%)',
                })}
                id="rate"
                name="rate"
                type="number"
                min="0"
                max="0.4"
                ref={register(validationSchema.rate)}
                error={formatError(errors.rate, validationSchema.rate, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.numberOfYearsLabel', {
                  defaultValue: 'Loan term (typically 15 or 30 years)',
                })}
                id="term"
                name="term"
                type="number"
                min="1"
                max="40"
                ref={register(validationSchema.term)}
                error={formatError(errors.term, validationSchema.term, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.closingCostsLabel', {
                  defaultValue: 'Closing costs (typically 2% to 5% of house price) ($)',
                })}
                id="closingCosts"
                name="closingCosts"
                type="number"
                min="0"
                ref={register(validationSchema.closingCosts)}
                error={formatError(errors.closingCosts, validationSchema.closingCosts, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualHomeownerLabel', {
                  defaultValue: 'Annual homeowners insurance premium ($)',
                })}
                id="homeowners"
                name="homeowners"
                type="number"
                min="1"
                ref={register(validationSchema.homeowners)}
                error={formatError(errors.homeowners, validationSchema.homeowners, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualPropertyTaxesLabel', {
                  defaultValue: 'Annual property taxes ($)',
                })}
                id="taxes"
                name="taxes"
                type="number"
                min="0"
                ref={register(validationSchema.taxes)}
                error={formatError(errors.taxes, validationSchema.taxes, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.annualMaintenanceCostLabel', {
                  defaultValue: 'Annual maintenance cost ($)',
                })}
                id="maint"
                name="maint"
                type="number"
                ref={register(validationSchema.maint)}
                error={formatError(errors.maint, validationSchema.maint, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('RentOrBuyCalculator.thirdLegend', {
              defaultValue: 'Other assumptions',
            })}
          </legend>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.numberOfYearComparisonLabel', {
                  defaultValue: 'Number of years for comparison',
                })}
                id="analysis"
                name="analysis"
                type="number"
                min="1"
                max="30"
                ref={register(validationSchema.analysis)}
                error={formatError(errors.analysis, validationSchema.analysis, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.marginalTaxBracketLabel', {
                  defaultValue: 'Marginal tax bracket (0% to 75%)',
                })}
                id="taxBracket"
                name="taxBracket"
                type="number"
                min="0"
                max="0.75"
                ref={register(validationSchema.taxBracket)}
                error={formatError(errors.taxBracket, validationSchema.taxBracket, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.beforeTaxLabel', {
                  defaultValue: 'Before tax return on savings (-12% to 12%)',
                })}
                id="beforeTaxReturn"
                name="beforeTaxReturn"
                type="number"
                min="-.12"
                max=".12"
                ref={register(validationSchema.beforeTaxReturn)}
                error={formatError(errors.beforeTaxReturn, validationSchema.beforeTaxReturn, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RentOrBuyCalculator.assumedAnnualInflationRateLabel', {
                  defaultValue: 'Assumed annual inflation rate (0% to 10%)',
                })}
                id="inf"
                name="inf"
                type="number"
                min="0"
                max=".10"
                ref={register(validationSchema.inf)}
                error={formatError(errors.inf, validationSchema.inf, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <Select
                label={t('RentOrBuyCalculator.standardDeductionLabel', {
                  defaultValue: 'On taxes, do you take the standard deduction?',
                })}
                id="standardDeduction"
                name="standardDeduction"
                ref={register(validationSchema.standardDeduction)}
                error={formatError(errors.standardDeduction, validationSchema.standardDeduction, t)}
              >
                <Select.Option value="N">
                  {t('RentOrBuyCalculator.standardDeductionOptions.no', {
                    defaultValue: 'No, I itemize',
                  })}
                </Select.Option>
                <Select.Option value="Y">
                  {t('RentOrBuyCalculator.standardDeductionOptions.yes', {
                    defaultValue: 'Yes',
                  })}
                </Select.Option>
              </Select>
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('RentOrBuyCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('RentOrBuyCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('RentOrBuyCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}
          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('RentOrBuyCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('RentOrBuyCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>
          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('RentOrBuyCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>
          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('RentOrBuyCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th className="section" colSpan={2}>
                  {t('RentOrBuyCalculator.rentVsBuy', {
                    defaultValue: 'Rent vs. buy analysis',
                  })}
                </th>
                <th scope="col">
                  {t('RentOrBuyCalculator.rent', {
                    defaultValue: 'Rent',
                  })}
                </th>
                <th scope="col">
                  {t('RentOrBuyCalculator.buy', {
                    defaultValue: 'Buy',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.rentAndFees', {
                    defaultValue: 'Rent and fees',
                  })}
                </th>
                <td />
                <td>{calcResults.rentTotal}</td>
                <td />
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.mortgagePayments', {
                    defaultValue: 'Mortgage payments',
                  })}
                </th>
                <td />
                <td />
                <td>{calcResults.paymentsTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.closingCosts', {
                    defaultValue: 'Closing costs',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.closingCostsTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.propertyInsurance', {
                    defaultValue: 'Property insurance',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.insuranceTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.propertyTaxes', {
                    defaultValue: 'Property taxes',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.taxesTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.maintenance', {
                    defaultValue: 'Maintenance',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.maintenanceTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.opportunityCost', {
                    defaultValue: 'Opportunity cost (tied-up equity)',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.oppcostTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.taxSavings', {
                    defaultValue: 'Tax savings (interest/taxes)',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>-</td>
                <td />
                <td>{calcResults.taxsavingsTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.appreciation', {
                    defaultValue: 'Appreciation',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>-</td>
                <td />
                <td>{calcResults.appreciationTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.principalReduction', {
                    defaultValue: 'Principal reduction',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>-</td>
                <td />
                <td>{calcResults.principalTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.realtorFeesOnFutureSale', {
                    defaultValue: 'Realtor fees on future sale',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>+</td>
                <td />
                <td>{calcResults.realtorFeesTotal}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.totalCost', {
                    defaultValue: 'Total cost',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}>=</td>
                <td>{calcResults.totalcostRent}</td>
                <td>{calcResults.totalcostBuy}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.presentValueAtInflation', {
                    defaultValue: 'Present value at inflation',
                  })}
                </th>
                <td />
                <td>{calcResults.pvtotalcostRent}</td>
                <td>{calcResults.pvtotalcostBuy}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RentOrBuyCalculator.difference', {
                    defaultValue: 'Difference',
                  })}
                </th>
                <td />
                <td />
                <td>{calcResults.difference}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('RentOrBuyCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('RentOrBuyCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { RentOrBuyCalculator }
export default RentOrBuyCalculator
